import React, { useEffect } from "react"
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    MenuItem,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import InfoIcon from "@material-ui/icons/Info"
import LockIcon from "@material-ui/icons/Lock"
import SaveIcon from "@material-ui/icons/Save"
import { useSnackbar } from "../../../utils/notificacoes"
import { ParametrosClienteFielActionsTypes } from "../context/parametrosClienteFielActions"
import { useParametrosClienteFielContext } from "../context/useParametrosClienteFielContext"
import { GRUPOS_CHECKBOXES_CF } from "../models/parametrosClienteFielModel"
import type { ParametrosClienteFielModel } from "../models/parametrosClienteFielModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "900px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#fff3f3",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #ffcdd2",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#c62828",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#e53935",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#c62828",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#e53935",
        color: "white",
        fontWeight: 600,
        padding: "10px 32px",
        boxShadow: "0 4px 12px rgba(229,57,53,0.3)",
        "&:hover": { backgroundColor: "#c62828", boxShadow: "0 6px 16px rgba(229,57,53,0.4)" },
    },
    checkboxLabel: {
        color: "#2c3e50",
    },
    checkboxRow: {
        display: "flex",
        alignItems: "center",
    },
    infoIcon: {
        fontSize: 16,
        color: "#90a4ae",
        marginLeft: theme.spacing(0.5),
        cursor: "pointer",
    },
    coordsText: {
        color: "#546e7a",
        fontSize: "0.85rem",
    },
}))

export function FormularioParametrosClienteFiel() {
    const classes = useStyles()
    const { parametros, dispatch, salvar, erroCarregamento } = useParametrosClienteFielContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handleCampo = (campo: keyof ParametrosClienteFielModel, valor: string | number | boolean | null) => {
        dispatch({ type: ParametrosClienteFielActionsTypes.ATUALIZAR_CAMPO, payload: { campo, valor } })
    }

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Parâmetros salvos com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao salvar", "error")
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    <LockIcon style={{ verticalAlign: "middle", marginRight: 8 }} />
                    Parâmetros — APENAS CLIENTE FIEL
                </Typography>

                {/* Delivery */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Delivery
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Tipo de taxa de entrega"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.calculoEntrega}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("calculoEntrega", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={0}>Por Bairro (Padrão)</MenuItem>
                                    <MenuItem value={1}>Por distância</MenuItem>
                                    <MenuItem value={2}>Por Raio</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Taxa de entrega padrão (R$)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, step: "0.01" }}
                                    value={parametros.taxaEntregaPadrao ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("taxaEntregaPadrao", v === "" ? null : parseFloat(v))
                                    }}
                                />
                            </Grid>

                            {(parametros.latitude !== null || parametros.longitude !== null) && (
                                <Grid item xs={12}>
                                    <Typography className={classes.coordsText}>
                                        Coordenadas: Lat {parametros.latitude ?? "—"} / Long{" "}
                                        {parametros.longitude ?? "—"}
                                    </Typography>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Paper>

                {/* Impressão */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Impressão
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Tipo de Impressão"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.tipoImpressao}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("tipoImpressao", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={0}>Delivery</MenuItem>
                                    <MenuItem value={1}>Sushi Japa (Cozinha/SushiBar)</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Aviso de impressão"
                                    placeholder="Ex: seu pedido será liberado somente com a sua assinatura física no ato da entrega."
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 500 }}
                                    value={parametros.avisoImpressao ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("avisoImpressao", v === "" ? null : v)
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Mesa */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Mesa
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Tipo de Pedido da Mesa"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.tipoPedidoMesa}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("tipoPedidoMesa", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={0}>Fazer Pedido</MenuItem>
                                    <MenuItem value={1}>Apenas visualizar cardápio</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Fidelidade */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Fidelidade
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Tipo de Pontuação"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.tipoPontuacao}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("tipoPontuacao", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={0}>Por valor consumido</MenuItem>
                                    <MenuItem value={1}>Por pedido</MenuItem>
                                    <MenuItem value={2}>Não pontuar no delivery</MenuItem>
                                </TextField>
                            </Grid>

                            {parametros.tipoPontuacao === 0 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        label="Valor por Selo (R$)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, step: "0.01" }}
                                        value={parametros.valorPorSelo ?? ""}
                                        onChange={e => {
                                            const v = e.target.value
                                            handleCampo("valorPorSelo", v === "" ? null : parseFloat(v))
                                        }}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Fator Multiplicador de Selos"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 1, max: 100, step: 1 }}
                                    value={parametros.fatorMultiplicadorSelos}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("fatorMultiplicadorSelos", v === "" ? 1 : parseInt(v))
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Tipo de Prêmio"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.tipoPremio}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("tipoPremio", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={-1}>Sem prêmio para resgate automático</MenuItem>
                                    <MenuItem value={0}>Desconto em reais aplicado no próximo pedido</MenuItem>
                                    <MenuItem value={1}>
                                        Desconto percentual em relação à média dos pedidos da cartela completa
                                    </MenuItem>
                                    <MenuItem value={2}>Percentual em relação ao valor do próximo pedido</MenuItem>
                                </TextField>
                            </Grid>

                            {parametros.tipoPremio === 0 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        label="Valor do Prêmio (R$)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, step: "0.01" }}
                                        value={parametros.valorPremio ?? ""}
                                        onChange={e => {
                                            const v = e.target.value
                                            handleCampo("valorPremio", v === "" ? null : parseFloat(v))
                                        }}
                                    />
                                </Grid>
                            )}

                            {parametros.tipoPremio === 1 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        label="Valor do Prêmio % (Média)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, max: 100, step: "0.01" }}
                                        value={parametros.valorPremioPercentualMedia ?? ""}
                                        onChange={e => {
                                            const v = e.target.value
                                            handleCampo(
                                                "valorPremioPercentualMedia",
                                                v === "" ? null : parseFloat(v)
                                            )
                                        }}
                                    />
                                </Grid>
                            )}

                            {parametros.tipoPremio === 2 && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField
                                        label="Valor do Prêmio % (Próximo Pedido)"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        type="number"
                                        inputProps={{ min: 0, max: 100, step: "0.01" }}
                                        value={parametros.valorPremioPercentual ?? ""}
                                        onChange={e => {
                                            const v = e.target.value
                                            handleCampo("valorPremioPercentual", v === "" ? null : parseFloat(v))
                                        }}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Valor mínimo de consumo por Selo (R$)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, step: "0.01" }}
                                    value={parametros.valorConsumoMinimoParaSelo ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("valorConsumoMinimoParaSelo", v === "" ? null : parseFloat(v))
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Contato / Comunicação */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Contato e Comunicação
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Destinatário Avaliação (email)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 200 }}
                                    value={parametros.destinatarioAvaliacao ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("destinatarioAvaliacao", v === "" ? null : v)
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Destinatário Faturas (email)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 200 }}
                                    value={parametros.destinatarioFaturas ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("destinatarioFaturas", v === "" ? null : v)
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Destinatário Pedidos (email)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 200 }}
                                    value={parametros.destinatarioPedidos ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("destinatarioPedidos", v === "" ? null : v)
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Tipo de Envio Nova Senha"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.tipoEnvioNovaSenha}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("tipoEnvioNovaSenha", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={1}>SMS</MenuItem>
                                    <MenuItem value={2}>WhatsApp</MenuItem>
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Sistema */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Sistema
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    select
                                    label="Aba Padrão Tela Fidelidade"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={parametros.abaPadraoTelaFidelidade}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("abaPadraoTelaFidelidade", parseInt(v))
                                    }}
                                >
                                    <MenuItem value={0}>CPF e/ou QRCODE</MenuItem>
                                    <MenuItem value={1}>Telefone e Valor Consumido</MenuItem>
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <Tooltip title="Útil para esconder conjunto de funcionalidades independente de usuários: GRUPO_00 = Não esconde itens, GRUPO_01 = Esconde funcionalidade do JAH_DO_ACAI">
                                    <TextField
                                        select
                                        label="Peculiaridades Estabelecimento/Rede"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={parametros.ocultarConfiguracoesEspecificas}
                                        onChange={e => {
                                            const v = e.target.value
                                            handleCampo("ocultarConfiguracoesEspecificas", parseInt(v))
                                        }}
                                    >
                                        <MenuItem value={0}>GRUPO_0 (Padrão)</MenuItem>
                                        <MenuItem value={1}>GRUPO_01 (JAH_DO_ACAI)</MenuItem>
                                        <MenuItem value={2}>GRUPO_02</MenuItem>
                                    </TextField>
                                </Tooltip>
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Script inicial (Izza)"
                                    placeholder="Script inicial para funcionamento da Izza"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 2000 }}
                                    value={parametros.scriptInicial ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("scriptInicial", v === "" ? null : v)
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Grupos de checkboxes */}
                {GRUPOS_CHECKBOXES_CF.map(grupo => (
                    <Paper key={grupo.titulo} className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                {grupo.titulo}
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Grid container spacing={1}>
                                {grupo.campos.map(meta => (
                                    <Grid key={meta.campo} item xs={12} sm={6}>
                                        <Box className={classes.checkboxRow}>
                                            <FormControlLabel
                                                className={classes.checkboxLabel}
                                                control={
                                                    <Checkbox
                                                        checked={parametros[meta.campo] as boolean}
                                                        onChange={e => {
                                                            const checked = e.target.checked
                                                            handleCampo(meta.campo, checked)
                                                        }}
                                                        style={{ color: "#e53935" }}
                                                    />
                                                }
                                                label={meta.label}
                                            />
                                            {meta.descricao && (
                                                <Tooltip title={meta.descricao} placement="right">
                                                    <InfoIcon className={classes.infoIcon} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Paper>
                ))}

                <Box display="flex" justifyContent="center" mb={4}>
                    <Button
                        className={classes.saveButton}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSalvar}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
